import { gql, useQuery } from "@apollo/client";
import { Button, makeStyles, Paper, Typography } from "@material-ui/core";
import { usePollingIntervalConfig } from "client/utils/polling";
import InterviewerAvatar from "components/legacy/InterviewerAvatar";
import {
  SchedulerEventsPrisma,
  SchedulerEventsPrismaVariables,
} from "generated/schemaTypes";
import calendarIcon from "icons/calendar-colorful.svg";
import _ from "lodash";
import moment, { Moment } from "moment-timezone";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useTrackEngagement } from "react-context/legacy/TrackContentModuleEngagementContextProvider";
import useAnalytics from "react-hooks/useAnalytics";
import { ResourceCustomTheme } from "styles/config";
import { Event, Interviewer } from "types/legacy";

import NylasSchedulerModal, { SchedulerMode } from "./NylasSchedulerModal";

const SCHEDULER_EVENTS_PRISMA = gql`
  query SchedulerEventsPrisma($stageId: UUID!) {
    legacyStageForEventsFindUnique(where: { id: $stageId }) {
      id
      events(where: { hidden: false }) {
        id
        nylasEditHash
        eventTemplateId
        startTime
      }
    }
  }
`;

const useStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {
    ...theme.mixins.moduleStyles(theme),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  card: {
    padding: `${theme.spacing(4)}px ${theme.spacing(2)}px ${theme.spacing(
      4
    )}px ${theme.spacing(2)}px`,
    border: `1px solid rgba(0,0,0,0.1)`,
    borderRadius: 8,
    marginBottom: "1.5rem",
  },
  avatar: {
    width: 64,
    height: 64,
  },
  wrap: {
    padding: 20,
    width: "100%",
    height: "90vh",
    maxHeight: "720px",
  },
  infoHeader: {
    display: "flex",
    justifyContent: "stretch",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  actionContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  infoText: {
    margin: 0,
    marginLeft: theme.spacing(1),
  },
  calendarIcon: {
    width: 64,
    height: 64,
  },
  actionBtn: {
    marginLeft: theme.spacing(2),
  },
  bookBtn: {
    width: 180,
    marginLeft: theme.spacing(2),
  },
}));
type InterviewSchedulerProps = {
  stageId: string;
  candidateName: string;
  candidateEmail?: string;
  guideUrl: string;
  customerGreenhouseMaildropAddress: string;
  isSample?: boolean;
  availabilitySlug: string;
  minCancellationNotice: number;
  eventTemplateId: string;
  interviewer: Interviewer;
  recruiterEmailAddress: string;
  recruiterFirstName: string;
  events: Event[];
  refetch?(): void;
};

function InterviewScheduler({
  stageId,
  candidateName,
  candidateEmail,
  guideUrl,
  customerGreenhouseMaildropAddress,
  isSample = false,
  events,
  availabilitySlug,
  minCancellationNotice,
  eventTemplateId: schedulerEventTemplateId,
  recruiterEmailAddress,
  recruiterFirstName,
  interviewer: schedulerInterviewer,
  refetch,
}: InterviewSchedulerProps) {
  const analytics = useAnalytics();
  const { trackEngagement } = useTrackEngagement();
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [cancelWindowPassed, setCancelWindowPassed] = useState(true);
  const [schedulerMode, setSchedulerMode] = useState(SchedulerMode.Schedule);

  const matchingEventBookedOutsideScheduler = _.find(
    events,
    ({ eventTemplateId, nylasEditHash }) =>
      !nylasEditHash && schedulerEventTemplateId === eventTemplateId
  );

  const matchingEvent = _.find(
    events,
    ({ eventTemplateId, nylasEditHash }) =>
      nylasEditHash && eventTemplateId === schedulerEventTemplateId
  ) as Event | null;

  const { interviewSchedulerEvents: pollInterval } = usePollingIntervalConfig();
  const { data: newEventsData } = useQuery<
    SchedulerEventsPrisma,
    SchedulerEventsPrismaVariables
  >(SCHEDULER_EVENTS_PRISMA, {
    variables: {
      stageId,
    },
    pollInterval,
    skip: isSample,
  });

  // Test latest events on a more frequent interval that our main
  // Stage data fetch polling. Critical for handling nylas webhooks
  // quickly enough for solid user experience
  useEffect(() => {
    if (newEventsData?.legacyStageForEventsFindUnique.events) {
      const latestMatchingEvent = _.find(
        newEventsData?.legacyStageForEventsFindUnique.events ?? [],
        ({ eventTemplateId, nylasEditHash }) =>
          !!nylasEditHash && eventTemplateId === schedulerEventTemplateId
      );

      if (
        (latestMatchingEvent && !matchingEvent) ||
        (!latestMatchingEvent && matchingEvent) ||
        latestMatchingEvent?.startTime !== matchingEvent?.startTime
      ) {
        // We need to refresh all the things
        refetch?.();
      }
    }
  }, [newEventsData, matchingEvent, schedulerEventTemplateId, refetch]);

  // Handle cancelled
  useEffect(() => {
    if (
      (schedulerMode === SchedulerMode.Cancel ||
        schedulerMode === SchedulerMode.Reschedule) &&
      !matchingEvent
    ) {
      setOpen(false);
      setSchedulerMode(SchedulerMode.Schedule);
    }
  }, [matchingEvent, schedulerMode]);

  const actualInterviewer = _.first(matchingEvent?.interviewers);

  const isBooked = matchingEvent;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let startTimeWithTimezone: Moment | undefined;
  let startTime: string | undefined;
  if (matchingEvent?.startTime) {
    startTimeWithTimezone = moment.tz(events[0].startTime, timezone);
    startTime = `${startTimeWithTimezone.format(
      "MMMM Do"
    )} at ${startTimeWithTimezone.format(
      "h:mm a"
    )} ${startTimeWithTimezone.format("z")}`;
  }

  const rescheduleMailToUrl = recruiterEmailAddress
    ? ` mailto:${recruiterEmailAddress}?bcc=${customerGreenhouseMaildropAddress}&subject=Unavailable%20for%20upcoming%20interview&body=${
        recruiterFirstName ? `Dear%20${recruiterFirstName}` : "Hi there"
      }%2C%0D%0A%0D%0AI'm%20unavailable%20for%20my%20upcoming%20interview%20(${guideUrl})%20and%20would%20like%20to%20reschedule%20for%20another%20time.%0D%0A%0D%0AWhat%20does%20your%20availability%20look%20like%20in%20the%20next%20few%20days%3F%0D%0A%0D%0AThank%20you!
  `
    : "";

  const cancelOrRescheduleViaEmail = () => {
    analytics.track("Reschedule or Cancel Interview via Email Clicked");
    window.open(rescheduleMailToUrl, "_blank");
  };

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateWindow = () =>
      setCancelWindowPassed(
        _.isUndefined(startTimeWithTimezone)
          ? true
          : startTimeWithTimezone!
              .subtract(minCancellationNotice, "minutes")
              .isBefore(moment())
      );

    if (!intervalRef.current) {
      updateWindow();
      intervalRef.current = setInterval(() => {
        updateWindow();
      }, 60000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [startTimeWithTimezone, minCancellationNotice, setCancelWindowPassed]);

  const reschedule = () => {
    trackEngagement?.({ event: "Reschedule Interview Clicked" });
    setSchedulerMode(SchedulerMode.Reschedule);
    setOpen(true);
  };

  const cancel = () => {
    trackEngagement?.({ event: "Cancel Interview Clicked" });
    setSchedulerMode(SchedulerMode.Cancel);
    setOpen(true);
  };

  const avatarInterviewer = actualInterviewer || schedulerInterviewer;

  const AvatarComponent = avatarInterviewer ? (
    <InterviewerAvatar
      interviewer={{
        ...avatarInterviewer,
        firstName: avatarInterviewer.firstName ?? "",
        lastName: avatarInterviewer.lastName ?? "",
        imageUrl: avatarInterviewer.imageUrl ?? "",
      }}
      className={classes.avatar}
    />
  ) : null;

  if (isSample) {
    return (
      <div className={classes.card}>
        <div className={classes.infoHeader}>
          {AvatarComponent}
          <Typography variant="h4" className={classes.infoText}>
            It&apos;s time to schedule your upcoming interview{" "}
            {avatarInterviewer ? `with ${avatarInterviewer.firstName}` : ""}
          </Typography>
        </div>
        <div className={classes.actionContainer}>
          <Image
            src={calendarIcon}
            className={classes.calendarIcon}
            alt="Calendar"
            layout="fixed"
            width="64px"
            height="64px"
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.actionBtn}
            onClick={() => setOpen(true)}
          >
            Book a Time
          </Button>
        </div>
      </div>
    );
  }

  if (matchingEventBookedOutsideScheduler) {
    return null;
  }

  return (
    <div className={classes.root}>
      {isBooked ? (
        <Paper elevation={1} className={classes.card}>
          <div className={classes.infoHeader}>
            {AvatarComponent}
            <Typography variant="h4" className={classes.infoText}>
              You’re all set to meet with{" "}
              {avatarInterviewer?.firstName ?? "your interviewer"} on{" "}
              {startTime}
            </Typography>
          </div>
          <div className={classes.actionContainer}>
            <Image
              src={calendarIcon}
              className={classes.calendarIcon}
              alt="Calendar"
              layout="fixed"
              width="64px"
              height="64px"
            />
            {cancelWindowPassed ? (
              <Button
                variant="contained"
                color="primary"
                className={classes.actionBtn}
                onClick={cancelOrRescheduleViaEmail}
              >
                Cancel or Reschedule
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.actionBtn}
                  onClick={reschedule}
                >
                  Find a Better Time
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  className={classes.actionBtn}
                  onClick={cancel}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </Paper>
      ) : (
        <Paper elevation={2} className={classes.card}>
          <div className={classes.infoHeader}>
            {AvatarComponent}
            <Typography variant="h4" className={classes.infoText}>
              It&apos;s time to schedule your upcoming interview{" "}
              {avatarInterviewer ? `with ${avatarInterviewer.firstName}` : ""}
            </Typography>
          </div>
          <div className={classes.actionContainer}>
            <Image
              src={calendarIcon}
              className={classes.calendarIcon}
              alt="Calendar"
              layout="fixed"
              width="64px"
              height="64px"
            />
            <Button
              variant="contained"
              color="primary"
              className={(classes.actionBtn, classes.bookBtn)}
              onClick={() => {
                trackEngagement?.({ event: "Book Interview Clicked" });
                setOpen(true);
              }}
            >
              Book a Time
            </Button>
          </div>
        </Paper>
      )}
      {availabilitySlug && (
        <NylasSchedulerModal
          mode={schedulerMode}
          slug={availabilitySlug}
          editHash={matchingEvent?.nylasEditHash ?? ""}
          candidateEmail={candidateEmail}
          candidateName={candidateName}
          stageId={stageId}
          open={open}
          onClose={() => setOpen(false)}
          onRedirect={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export default InterviewScheduler;
